import React from 'react';
import moment from 'moment';
import {
  compact,
  orderBy,
  sortBy,
  startCase,
  upperFirst,
  gt,
  toNumber,
  trim,
} from 'lodash';
import validator from 'validator';


import {
  animateScroll,
  scroller,
} from 'react-scroll';
import ExpandIcon from '@material-ui/icons/KeyboardArrowDown';
import CollapseIcon from '@material-ui/icons/KeyboardArrowUp';
import Sort from '@material-ui/icons/UnfoldMore';
import { AvAddToQueue } from 'material-ui/svg-icons';

export const appendDollar = label => `${label}($)`;

// sorting functions
function desc(a, b, orderBy) {
  const value1 = toNumber(a[orderBy]) || a[orderBy];
  const value2 = toNumber(a[orderBy]) || b[orderBy];
  if (gt(value1, value2)) {
    return -1;
  }
  if (gt(value2, value1)) {
    return 1;
  }
  return 0;
}

// default sorting based on the category, name, and subname
function applyDefaultSortComparisons(array) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    if (!a.name || !b.name) return 0;
    return a.category.localeCompare(b.category) ||
        a.name.localeCompare(b.name) ||
        a.subname.localeCompare(b.subname);  
  });
  return stabilizedThis.map(el => el[0]);
};

export function stableSort(array, cmp) {
  // if no valid sort is specified, apply default sorts
  if (!cmp || cmp.length === 1) {    
    return applyDefaultSortComparisons(array);    
  } else {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = cmp(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  }
}

export function getSorting(order, orderBy) {
  if (!orderBy || orderBy.length === 0) {
    return null;
  }
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}
// end of sorting functions
export const bufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;

  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

export const dispatchMaker = methods => (dispatch) => {
  const methodsGroup = {};

  methods.forEach((method) => {
    methodsGroup[method[1]] = (...args) => dispatch(method[0](...args));
  });
  return methodsGroup;
};

export const dispatchRestMaker = (restActionsList = []) => {
  const methods = [];

  restActionsList.forEach(([restActions, name]) => {
    const capitalizedName = upperFirst(name);
    methods.push([restActions.create, `create${capitalizedName}`]);
    methods.push([restActions.update, `update${capitalizedName}`]);
    methods.push([restActions.list, `list${capitalizedName}s`]);
    methods.push([restActions.read, `read${capitalizedName}`]);
    methods.push([restActions.remove, `remove${capitalizedName}`]);
  });
  return dispatchMaker(methods);
};

export const formatPhoneNumber = (s) => {
  if (!s) return '';
  const s2 = s.replace(/\D/g, '');
  const m = s2.match(/^(\d{3})(\d{3})(\d{4})$/);

  return (!m) ? null : `(${m[1]}) ${m[2]}-${m[3]}`;
};

export const formatPrice = price => Number(price).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const isDistributor = currentUserRoleName =>
  (currentUserRoleName === 'Distributor');

export const isFarmer = currentUserRoleName =>
  (currentUserRoleName === 'Seller');

export const isRestaurant = currentUserRoleName =>
  (currentUserRoleName === 'Buyer');

export const isConsumer = currentUserRoleName =>
  (currentUserRoleName === 'Consumer');

export const getInitials = (name = '') => {
  if (name.trim().split(' ').length === 1) {
    return name.substring(0, 2).toUpperCase();
  }
  return name.split(' ').map(subname => subname[0] && subname[0].toUpperCase()).slice(0, 2).join('');
};

export const getFullNameOrEmail = (user) => {
  let fullNameOrEmail = '';
  const { profile } = user;
  if (profile.firstName || profile.lastName) {
    fullNameOrEmail = `${profile.firstName} ${profile.lastName}`;
  } else {
    fullNameOrEmail = user.email;
  }
  const result = user.currentRoleId ? profile.companyName : fullNameOrEmail;

  return result;
};

export const getOrderStatus = (order) => {
  if (order.invalid) return 'Invalid';
  if (order.noPayment && order.delivered) return 'Delivered';
  if (order.noPayment && order.authorized) return 'Confirmed';
  if (order.noPayment && order.confirmed) return '';
  // if (order.captured && order.authorized) return 'Paid';
  if (order.authorized) return 'Paid';
  if (order.manualPay) return 'UnPaid';
  if (order.confirmed) return 'Confirmed';
  return '';
};

export const getCaptureMoment = (order) => {
  if (order.deliveredAt || !order.captureScheduledAt) return '';
  return ` > ${moment(order.captureScheduledAt).format('MM/DD')}`;
};

export const getSourceFieldByType = (type, formatMessage) => {
  if (type === 'SO' || type === 'PO') {
    return {
      label: formatMessage('Vendor'),
      name: 'source_business_name',
      colSpan: 2,
    };
  }

  return {
    label: formatMessage('Customer'),
    name: 'buyer_business_name',
    colSpan: 2,
  };
};

export const includesSearchText = (searchText, items) => {
  if (!searchText) return true;
  const searchTextLower = searchText.toLowerCase();
  const searchTexts = searchTextLower.trim().split(' ');

  for (let j = 0; j < searchTexts.length; j++) {
    let found = false;
    for (let i = 0; i < items.length; i++) {
      if (!items[i]) continue;
      if (items[i].toLowerCase().indexOf(searchTexts[j]) > -1) {
        found = true;
        break;
      }
    }
    if (!found) return false;
  }

  return true;
};

export const modifyString = (currentString) => {
  if (currentString.search('STEMMED ') === 0) {
    return currentString.substring(8);
  }

  if (currentString.search('MEAT, FISH AND POULTRY') === 0) {
    return 'MEAT, FISH, AND POULTRY';
  }

  return currentString;
};

export const shortenDuration = (duration) => {
  const patterns = [['second', 'sec'], ['minute', 'min'], ['hour', 'hr'], ['month', 'mon'], ['year', 'yr']];
  return patterns.reduce((r, p) => r.replace(p[0], p[1]), duration);
};

export const toDefaultDateTimeFormat = date => moment(date).local().format('MMM DD, hh:mm A').toString();
export const toDefaultDateTimeFormatForBottom = date => moment(date).local().format('DD MMM, hh:mm A').toString();

export const toISO = date => moment(date).local().format('YYYY-MM-DD');
export const toDefaultISO = date => moment(date).local().format('MM/DD/YYYY');

export const toLongDateFormat = date => moment(date).local().format('DD MMM, YYYY');
export const toLongDateFormatWithOutComma = date => moment(date).local().format('DD MMM YYYY');

export const toShortDateFormat = date => moment(date).local().format('DD MMM, YY');
export const toShortestDateFormat = date => moment(date).local().format('DD MMM');
export const toShortestDefaultDateFormat = date => moment(date).local().format('MMM DD');
export const toLongestDateFormat = date => moment(date).local().format('LL');
export const toShortestDateFormatMonthFirst = date => moment(date).local().format('MMM DD');

export const toShortDateTimeFormat = date => moment(date).local().format('DD MMM YY @HH:mm');
export const toShortDateTimeFormatForPaymentInfo = date => moment(date).local().format('DD MMM @ HH:mm');

export const toShortTimeFormat = date => moment(date).local().format('hh:mm A');
export const toDefaultTimeFormat = date => moment(date).local().format('HH:mm');
export const toStartCase = str => str.toLowerCase().split(', ').map(substr => startCase(substr)).join(', ');
export const toStartCaseWithSlash = str => str.toLowerCase().split('/').map(substr => startCase(substr)).join('/');

export const toCurrency = str => (parseFloat(str, 10) >= 0 ? `$${formatPrice(parseFloat(str, 10))}` : `-$${formatPrice(Math.abs(parseFloat(str, 10)))}`);

export const scrollTo = (id = 'main', {
  containerId = 'main',
  isAnimateScroll = false,
  topOrBottom = 'top',
} = {}) => {
  const options = {
    containerId: isAnimateScroll ? id : containerId,
    duration: 500,
    smooth: true,
  };
  if (!isAnimateScroll) {
    scroller.scrollTo(id, options);
    return;
  }
  if (topOrBottom === 'top') {
    animateScroll.scrollToTop(options);
  } else {
    animateScroll.scrollToBottom(options);
  }
};

export const getValidatedPackrows = (packrows, isStoreItem = false) => (
  packrows.map(({ cost, price, availableQuantity, ...rest }) => ({
    ...rest,
    invalid: !isStoreItem && (parseFloat(availableQuantity, 10) <= 0 ||
      (parseFloat(cost, 10) >= parseFloat(price, 10))),
    cost: isStoreItem ? 0 : parseFloat(cost, 10),
    price: parseFloat(price, 10),
    availableQuantity: parseFloat(availableQuantity, 10),
  }))
);

export const getDeliveryScheduleStatus = (deliverySchedule, date) => {
  const yesterday = new Date();
  const today = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const dayEnabled = deliverySchedule.findIndex(
    schedule =>
      schedule.type === 'day' &&
      schedule.day === date.getDay(),
  ) !== -1;
  const dateEnabled = deliverySchedule.findIndex(
    schedule =>
      schedule.type === 'date' &&
      schedule.enabled &&
      schedule.date === date.getDate() &&
      schedule.month === date.getMonth() &&
      schedule.year === date.getFullYear(),
  ) !== -1;
  const dateDisabled = deliverySchedule.findIndex(
    schedule =>
      schedule.type === 'date' &&
      !schedule.enabled &&
      schedule.date === date.getDate() &&
      schedule.month === date.getMonth() &&
      schedule.year === date.getFullYear(),
  ) !== -1;
  const old = (date.getTime() < yesterday.getTime());
  const now = (date.getTime() === today.getTime());
  return {
    old,
    now,
    dayEnabled,
    dateEnabled,
    dateDisabled,
    enabled: (dayEnabled || dateEnabled) && !dateDisabled,
  };
};

export const getPickedAvailableDate = (deliverySchedule, date) => {
  const dateString = moment(date).format('YYYY-MM-DD');
  const select = deliverySchedule.indexOf(dateString) !== -1;
  const enabled = moment(dateString).isAfter(moment(new Date()).add(-1, 'days').format('YYYY-MM-DD'));
  return {
    select,
    enabled,
  };
};

export const isDateOfDeliveryWeeks = (deliverySchedule, date) => {  
  const numberOfWeek = 7;
  const yesterday = moment(new Date()).add(-1, 'days').toDate();
  const beginDatesOfWeeks = [];
  const datesOfDeliveryWeeks = [];

  // START single dates check
  const enabledDeliveryDates = deliverySchedule.filter(x => x.enabled && x.month === date.getMonth());

  // set begin dates of weeks
  for (let i = 0; i < enabledDeliveryDates.length; i++) {
    if (enabledDeliveryDates[i].value) {
      const dateItem = moment(enabledDeliveryDates[i].value).toDate();
      const beginOfWeek = getBeginOfWeek(dateItem, 'US');
      if (beginDatesOfWeeks.indexOf(moment(beginOfWeek).format('YYYY-MM-DD')) === -1) {
        beginDatesOfWeeks.push(moment(beginOfWeek).format('YYYY-MM-DD'));
      }
    }
  }

  // set dates in each of weeks have enabled dates
  for (let i = 0; i < beginDatesOfWeeks.length; i++) {
    // check old dates
    let notOldDatesCount = 0;
    for (let j = 0; j < numberOfWeek; j++) {
      const oldDay = (moment(beginDatesOfWeeks[i]).add(j, 'days').toDate().getTime() < yesterday.getTime());
      if (!oldDay) {
        notOldDatesCount++;
      }
    }

    // set dates will be shown if has at least one not old dates
    if (notOldDatesCount > 0) {
      for (let j = 0; j < numberOfWeek; j++) {
        datesOfDeliveryWeeks.push(moment(beginDatesOfWeeks[i]).add(j, 'days').format('YYYY-MM-DD'));
      }
    }
  }
  // END single dates check

  // START repeat (every) dates check
  const thisMonth = moment(date).month();
  const thisDateItem = moment(date).toDate();
  const thisBeginOfWeek = getBeginOfWeek(thisDateItem, 'US');
  if (beginDatesOfWeeks.indexOf(moment(thisBeginOfWeek).format('YYYY-MM-DD')) === -1) {
    beginDatesOfWeeks.push(moment(thisBeginOfWeek).format('YYYY-MM-DD'));
  }

  // set dates in each of weeks have enabled dates
  for (let i = 0; i < beginDatesOfWeeks.length; i++) {
    // check old dates
    let notOldDatesCount = 0;
    for (let j = 0; j < numberOfWeek; j++) {
      const oldDay = (moment(beginDatesOfWeeks[i]).add(j, 'days').toDate().getTime() < yesterday.getTime());
      if (!oldDay) {
        notOldDatesCount++;
      }
    }
    // check set dates
    let repeatDatesCount = 0;
    for (let j = 0; j < numberOfWeek; j++) {
      const checkDay = moment(beginDatesOfWeeks[i]).add(j, 'days').toDate();
      const dayEnabled = deliverySchedule.findIndex(
        schedule =>
          schedule.type === 'day' &&
          schedule.day === checkDay.getDay(),
      ) !== -1;
      if (dayEnabled && checkDay.getMonth() === thisMonth) {
        repeatDatesCount++;
      }
    }

    // set dates will be shown if has at least one not old dates and repeat dates
    if (notOldDatesCount > 0 && repeatDatesCount > 0) {
      for (let j = 0; j < numberOfWeek; j++) {
        datesOfDeliveryWeeks.push(moment(beginDatesOfWeeks[i]).add(j, 'days').format('YYYY-MM-DD'));
      }
    }
  }
  // END repeat (every) dates check

  const isShownDate = datesOfDeliveryWeeks.indexOf(moment(date).format('YYYY-MM-DD')) !== -1;
  return isShownDate;
};

export const generatePublicIdentifier = businessName => (
  businessName ? businessName.replace(/([^0-9a-zA-Z]+)/g, ' ').trim().replace(/ /g, '_').toLowerCase() : ''
);

export const generatePublicIdentifierWithoutUnderscore = businessName => (
  businessName ? businessName.trim().replace(/ /g, '').toLowerCase() : ''
);

export const getMainUrl = () =>
  `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;

export const getMainUrlWithOutProtocol = () => {
  const host = window.location.hostname.replace(/(^www\.)/g, '');
  return `${host}${window.location.port ? `:${window.location.port}` : ''}`;
}
  
  
export const reorderTags = (items, sortedTags) => (
  items.map((item) => {
    const itemTags = item.tags;
    if (!itemTags || itemTags.length < 2) return item;

    const resultTags = sortBy(
      itemTags.map(tagName => sortedTags.find(tag => tag.name === tagName)),
      'order',
    ).map(tag => tag.name);

    return {
      ...item,
      tags: resultTags,
    };
  })
);

export const addTagValue = (items, sortedTags) =>
  items.map((item) => {
    const itemTags = item.tags || [];
    const resultTags = sortBy(
      itemTags.map(tagName => sortedTags.find(tag => tag.name === tagName)),
      'name',
    );

    return {
      ...item,
      tags: resultTags.map(tag => tag.name),
      tagValue: resultTags.map(tag => String(tag.order).padStart(3, '0')).join(''),
    };
  });

export const getOrganizationUID =
  name => trim(name.replace(/ /g, '').toLowerCase());

export const updatedClassname = (instance, key, value) => {
  if (instance[key] !== value) {
    instance[key] = value; // eslint-disable-line
    return 'update-animation';
  }
  return '';
};

export const getNextSortOptions = (sortOptions, latestSortColumn) => {
  // sortOptions are in the form of [['columnA', 'desc'], ['columnB', 'asc']]
  const index = sortOptions.map(o => o[0]).indexOf(latestSortColumn);
  let newSort;
  let options;

  if (index >= 0) {
    newSort = sortOptions[index];
    if (newSort[1] === 'desc') {
      newSort = null;
    } else {
      newSort[1] = 'desc';
    }

    options = [
      ...sortOptions.slice(0, index),
      ...sortOptions.slice(index + 1),
    ];
  } else {
    newSort = [latestSortColumn, 'asc'];
    options = sortOptions;
  }

  return compact([newSort, ...options]);
};

export const sortItems = (items, sortOptions, isCatalog = false, isWarehouse = false) => {
  const approved = r => r.productData.approved === false;
  const newlyApproved = r => r.productData.newlyApproved === true;
  const name = r => r.productData.name.toLowerCase();
  const subName = r => r.productData.subname.toLowerCase();
  const category = r => r.productData.category.toLowerCase();
  const count = r => Math.floor(r.count);
  const availableQuantity = r => parseInt(r.availableQuantity);
  const price = r => parseFloat(r.price);
  const isBulk = r => r.isBulk === false;
  const sortFn = {
    timestamp: 'updatedAt',
    alert: r => r.packrows.every(packrow =>
      parseInt(packrow.availableQuantity) >= 1 && packrow.price > packrow.cost,
    ),
  };
  return orderBy(items,
    compact([...sortOptions.map(o => sortFn[o[0]]), approved, 'approved', !isCatalog && !isWarehouse && 'sourceApproved', category, name, subName, 'tagValue', isBulk, count, availableQuantity, price]),
    compact([...sortOptions.map(o => o[1]), 'desc', 'desc', !isCatalog && !isWarehouse && 'desc', 'asc', 'asc', 'asc', 'asc', 'desc', 'asc', 'asc', 'asc']),
  );
};

const styles = {
  iconStyle: {
    color: 'grey',
    height: 15,
    width: 15,
  },
};

export const getSortIcon = (sortOptions, sortColumn) => {
  const index = sortOptions.map(o => o[0]).indexOf(sortColumn);
  const order = index >= 0 ? sortOptions[index][1] : 'none';

  return {
    asc: <CollapseIcon style={styles.iconStyle} />,
    desc: <ExpandIcon style={styles.iconStyle} />,
  }[order] || <Sort style={styles.iconStyle} />;
};

export const removeEmojis = (text) => {
  const ranges = [
    '\ud83c[\udf00-\udfff]', // U+1F300 to U+1F3FF
    '\ud83d[\udc00-\ude4f]', // U+1F400 to U+1F64F
    '\ud83d[\ude80-\udeff]', // U+1F680 to U+1F6FF
  ];

  return text.replace(new RegExp(ranges.join('|'), 'g'), '');
};

export const validateEmail = email => validator.isEmail(email);

export const packrowsWithOriginalValues = (packrows) => {
  return packrows.map(pr => {
    return { ...pr, originalValues: { count: pr.count, uom: pr.uom, pack: pr.pack } }
  });
};

// export function convertStringToArray(str) {
//   let array = [];
//   if (str) {
//     array = str.split(',').map(s => {
//       return String(s.trim());
//     });
//   }
//   return array;
// };

export function decimalFixed (number, fixed = 2) {
  if (isNaN(parseFloat(number, 10))) {
    return 0;
  }
  return Number(parseFloat(number, 10).toFixed(fixed));
}

export function getCartLocalStorage (invoice = { items: {} }) {
  const invoiceFromLocalStorage = JSON.parse(localStorage.getItem('invoice'));
  let invoiceForPublic = invoice;
  if (Object.keys(invoiceForPublic.items).length === 0 && invoiceFromLocalStorage) {
    invoiceForPublic = invoiceFromLocalStorage;
  }
  return invoiceForPublic;
}

export function setCartLocalStorage (invoiceForPublic = JSON.parse({ items: {} })) {
  localStorage.setItem('invoice', invoiceForPublic ? JSON.stringify(invoiceForPublic) : JSON.stringify({ items: {} }));
}

export function clearCartLocalStorage () {
  localStorage.setItem('invoice', JSON.stringify({ items: {} }));
}

export const boxItemsIds = [18364, 18331, 18307, 18466];

export function checkIsAddedABox (invoiceItems) {
  if (!invoiceItems || invoiceItems.length === 0) {
    return 0;
  }
  const orderedItems = Object.values(invoiceItems);
  const ids = boxItemsIds;
  const boxItemsCount = orderedItems && orderedItems.length > 0 ? orderedItems.filter(item => ids.indexOf(item.id) !== -1).length : 0;
  return boxItemsCount;
}

export const checkDayOnDeliveryWeek = (deliverySchedule, date) => { 
  
  deliverySchedule.forEach((item) => {
    const year = item.year;
    const monthIndex = item.month;
    const day = item.date;
    const today = new Date(year, monthIndex, day);
    const beginWeek = getBeginOfWeek(today);
    const endWeek = getEndOfWeek(today);

    //TODO. Change to use getTime() to compare
    //check if date between beginWeek and endWeek
    if (date.getDate() >= beginWeek.getDate()  &&
     date.getMonth() >= beginWeek.getMonth() &&
      date.getFullYear() ===  beginWeek.getFullYear() && 
      date.getDate() <= endWeek.getDate()  &&
      date.getMonth() <= endWeek.getMonth() &&
      endWeek.getFullYear() === date.getFullYear()) {
        return true;
    }
  });
  return false;
}

export const getDayOfWeek = (date, calendarType = 'ISO 8601') => {
  const weekday = date.getDay();

  switch (calendarType) {
    case 'ISO 8601':
      // Shifts days of the week so that Monday is 0, Sunday is 6
      return (weekday + 6) % 7;
    case 'Arabic':
      return (weekday + 1) % 7;
    case 'Hebrew':
    case 'US':
      return weekday;
    default:
      throw new Error('Unsupported calendar type.');
  }
};

export const getBeginOfWeek = (date, calendarType = 'ISO 8601') => {
  const year = getYear(date);
  const monthIndex = getMonthIndex(date);
  const day = date.getDate() - getDayOfWeek(date, calendarType);
  return new Date(year, monthIndex, day);
};

export const getEndOfWeek = (date, calendarType = 'ISO 8601') => {
  const year = getYear(date);
  const monthIndex = getMonthIndex(date);
  const day = date.getDate() + (6 - getDayOfWeek(date, calendarType));
  return new Date(year, monthIndex, day);
};


/* Simple getters - getting a property of a given point in time */

export const getYear = (date) => {
  if (date instanceof Date) {
    return date.getFullYear();
  }

  if (typeof date === 'number') {
    return date;
  }

  const year = parseInt(date, 10);

  if (typeof date === 'string' && !isNaN(year)) {
    return year;
  }

  throw new Error(`Failed to get year from date: ${date}.`);
};

export const getMonth = date => date.getMonth() + 1;

export const getMonthIndex = date => date.getMonth();

export const getDay = date => date.getDate();

export const getActiveStartDate = (deliverySchedule) => {
  const today = new Date();
  const thisMonth = today.getMonth();
  const nextMonth = moment(new Date()).add(1, 'month').toDate().getMonth();
  const repeatAvailableDays = deliverySchedule.filter(x => x.enabled && x.type === 'day' && x.date >= today.getDate());
  const availableDaysOfThisMonth = deliverySchedule.filter(x => x.enabled && x.month === thisMonth && x.date >= today.getDate());
  const availableDaysOfNextMonth = deliverySchedule.filter(x => x.enabled && x.month === nextMonth);
  if ((repeatAvailableDays && repeatAvailableDays.length > 0) ||
    (availableDaysOfThisMonth && availableDaysOfThisMonth.length > 0)) {
    return today;
  } else if (availableDaysOfNextMonth && availableDaysOfNextMonth.length > 0) {

    return moment(today).add(1, 'month').toDate();
  }

  return today;
};


